import type { ReactNode } from 'react';
import { ComplianceBar } from '@/shared/components/ComplianceBar';
import { ComplianceSummaryBadges } from '@/shared/components/ComplianceSummaryBadges';
import { StackedAmountsABCD } from '@/shared/components/StackedAmountsABCD';
import { getDefaultThresholdFromConfig } from '@/features/settings/utils/resolveDashboardSetting';
import { formatCurrencyCompact, round2AwayFromZero } from '@/shared/utils/number';

export interface BudgetCompliancePanelProps {
  BudgetSummary_ATotalMainCurruncy: number;
  BudgetSummary_BTotalMainCurruncy: number;
  BudgetSummary_CTotalMainCurruncy: number;
  BudgetSummary_DTotalMainCurruncy: number;
  threshold?: number;
  minimumPercent?: number;
  totalRevenueUSD?: number;
  totalExpensesUSD?: number;
  className?: string;
  settingSourceNote?: ReactNode;
}

function n(v: number | undefined): number {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
}

export function BudgetCompliancePanel({
  BudgetSummary_ATotalMainCurruncy: A,
  BudgetSummary_BTotalMainCurruncy: B,
  BudgetSummary_CTotalMainCurruncy: C,
  BudgetSummary_DTotalMainCurruncy: D,
  threshold = getDefaultThresholdFromConfig('Budget Law A+B Minimum Percent') / 100,
  minimumPercent = getDefaultThresholdFromConfig('Budget Law A+B Minimum Percent'),
  totalRevenueUSD,
  totalExpensesUSD,
  className = '',
  settingSourceNote,
}: BudgetCompliancePanelProps) {
  const a = n(A);
  const b = n(B);
  const c = n(C);
  const d = n(D);
  const abMain = a + b;
  const abcMain = a + b + c;
  const thr = Number.isFinite(threshold)
    ? threshold
    : getDefaultThresholdFromConfig('Budget Law A+B Minimum Percent') / 100;

  const abShareRaw = abcMain > 0 ? abMain / abcMain : 0;
  const abShare = round2AwayFromZero(abShareRaw);
  const cShare = round2AwayFromZero(1 - abShare);
  const compliantAB = abShare >= thr;

  const totalAbs = Math.abs(a) + Math.abs(b) + Math.abs(c) + Math.abs(d);

  if (abcMain === 0) {
    return (
      <div
        className={`rounded-xl border border-dashed border-slate-200 bg-slate-50/90 px-4 py-8 text-center text-sm text-slate-500 print:py-4 print:text-[8px] ${className}`}
        role="status"
        aria-live="polite"
      >
        Split unavailable. Please review budget inputs.
      </div>
    );
  }

  const hasRevenueExpense =
    totalRevenueUSD != null &&
    totalExpensesUSD != null &&
    Number.isFinite(totalRevenueUSD) &&
    Number.isFinite(totalExpensesUSD);

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="rounded-xl border border-slate-300/90 bg-gradient-to-b from-slate-50 to-white p-4 shadow-sm ring-1 ring-slate-200/80 print:border-slate-400 print:p-2">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800">Governed A+B vs C (rule)</h4>
        <p className="mt-1 text-[10px] leading-snug text-slate-500 print:text-[7px]">
          A+B and C shares use main-currency buckets only; shares are rounded to 2 decimals (away from zero) before
          comparing to the threshold. D is excluded from this rule.
        </p>
        <div className="mt-3">
          <ComplianceSummaryBadges compliantAB={compliantAB} minimumPercent={minimumPercent} />
        </div>
        <div className="mt-4">
          <ComplianceBar
            abShare={abShare}
            cShare={cShare}
            compliantAB={compliantAB}
            threshold={thr}
            aMain={a}
            bMain={b}
            cMain={c}
          />
        </div>
        {settingSourceNote ? <div className="mt-3">{settingSourceNote}</div> : null}
      </div>

      <div className="rounded-xl border border-slate-300/90 bg-white p-4 shadow-sm ring-1 ring-slate-200/80 print:border-slate-400 print:p-2">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800">Stacked amounts (A+B+C+D)</h4>
        <p className="mt-0.5 text-[10px] text-slate-500">
          Total |A|+|B|+|C|+|D| {formatCurrencyCompact(totalAbs)} · segment width by absolute amount
        </p>
        <div className="mt-3">
          <StackedAmountsABCD aMain={a} bMain={b} cMain={c} dMain={d} />
        </div>
      </div>

      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-3 print:py-2">
        <h4 className="text-[10px] font-bold uppercase tracking-wide text-slate-600">Revenues vs expenses (summary)</h4>
        {hasRevenueExpense ? (
          <div className="mt-2 flex flex-wrap gap-6 text-xs font-semibold text-slate-800 print:text-[8px]">
            <span>Revenue: {formatCurrencyCompact(totalRevenueUSD)}</span>
            <span>Expenses: {formatCurrencyCompact(totalExpensesUSD)}</span>
          </div>
        ) : (
          <p className="mt-2 text-xs text-slate-500 print:text-[8px]">
            Revenue and expense lines appear here when the dashboard provides totals for this school and year.
          </p>
        )}
      </div>
    </div>
  );
}
